import {
  fontStyles,
  Button,
  Card,
  Checkbox,
  Icon,
  IconButton,
  TextField,
} from '@monash/portal-react';
import c from './categories-input-card.module.scss';
import classNames from 'classnames';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import CategoriesDropdown from './categories-dropdown/CategoriesDropdown';
import { useStoreValue } from '@longsien/react-store';
import { categoriesStore } from '../../../shared/stores';
import {
  LINKS_INLINE_ERROR_MSGS,
  MAX_CATEGORY_WEIGHT,
  MIN_CATEGORY_WEIGHT,
} from '../../constants';
import InlineErrorMessage from '../../../../common/inline-error-message/InlineErrorMessage';
import SubcategoryInputs from './sub-category-inputs/SubcategoryInputs';

const EMPTY_SUB_CATEGORY_DATA = {
  id: '',
  weight: 0,
};

const getCategoryDropdownOptions = (categories) => {
  if (!categories) return [];

  return categories.map((category) => {
    const { title, id } = category;
    return { text: title, value: id };
  });
};

const CategoriesInputCard = ({ index, deleteCategoryData, disabled }) => {
  const {
    setValue,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const fieldNamePrefix = `categories.${index}`;
  const idFieldName = `${fieldNamePrefix}.id`;
  const subcategoriesFieldName = `${fieldNamePrefix}.subcategories`;

  const watchedId = useWatch({ name: idFieldName });

  const {
    fields: subcategories,
    append: appendSubcategory,
    remove: removeSubcategory,
  } = useFieldArray({
    name: subcategoriesFieldName,
  });

  const categories = useStoreValue(categoriesStore);
  const categoryDropdownOptions = getCategoryDropdownOptions(categories);

  const getError = () => {
    const categoryErrors = errors.categories?.[index];

    const idError = categoryErrors?.id;
    const weightError = categoryErrors?.weight;

    if (idError) return [idError, idError.message];
    if (weightError) return [weightError, weightError.message];
    return [null, null];
  };

  const [categoryError, categoryErrorMsg] = getError();

  return (
    <Card className={c.cardContainer}>
      <div className={c.categoryGrid}>
        {/* Category header */}
        <div className={classNames(fontStyles.label, c.label)}>Categories</div>
        <div className={classNames(fontStyles.label, c.label)}>Pinned</div>
        <div className={classNames(fontStyles.label, c.label)}>Weighting</div>
        <div inert />

        {/* Category inputs */}
        <Controller
          name={idFieldName}
          rules={{ required: LINKS_INLINE_ERROR_MSGS.CATEGORY }}
          render={({ field: { value, onChange, onBlur } }) => (
            <CategoriesDropdown
              index={index}
              value={value}
              onBlur={onBlur}
              dropdownOptions={categoryDropdownOptions}
              onChange={(e) => {
                onChange(e);
                setValue(subcategoriesFieldName, []); // Clear sub category values on change
                isSubmitted && trigger(idFieldName); // Manually validate b/c dropdown's onBlur doesn't validate on first blur
              }}
              disabled={disabled}
            />
          )}
        />
        <Controller
          name={`${fieldNamePrefix}.pinned`}
          render={({ field: { value, onChange, onBlur } }) => (
            <Checkbox
              value={value}
              on={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled || !watchedId}
            />
          )}
        />
        <Controller
          name={`${fieldNamePrefix}.weight`}
          rules={{
            min: {
              value: MIN_CATEGORY_WEIGHT,
              message: LINKS_INLINE_ERROR_MSGS.CATEGORY_WEIGHT,
            },
            max: {
              value: MAX_CATEGORY_WEIGHT,
              message: LINKS_INLINE_ERROR_MSGS.CATEGORY_WEIGHT,
            },
            validate: {
              isInteger: (value) => {
                if (!Number.isInteger(value))
                  return LINKS_INLINE_ERROR_MSGS.CATEGORY_WEIGHT;
              },
            },
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              value={value}
              onChange={(e) => onChange(parseInt(e.target.value))}
              onBlur={onBlur}
              type="number"
              min={MIN_CATEGORY_WEIGHT}
              max={MAX_CATEGORY_WEIGHT}
              disabled={disabled || !watchedId}
            />
          )}
        />
        <div className={c.deleteButtonContainer}>
          <IconButton
            icon={Icon.Trash}
            color="var(--card-text-color)"
            onClick={deleteCategoryData}
            aria-label="Delete category"
            disabled={disabled}
          />
        </div>
      </div>
      {categoryError && <InlineErrorMessage message={categoryErrorMsg} />}
      {subcategories.length > 0 && (
        <div className={c.subcategoriesGrid}>
          {/* Sub categories header */}
          <div className={classNames(fontStyles.label, c.label)}>
            Sub categories
          </div>
          <div className={classNames(fontStyles.label, c.label)}>Weighting</div>
          <div inert />

          {/* Sub categories rows */}
          {subcategories.map((subcategoryData, subcategoryIndex) => {
            return (
              <SubcategoryInputs
                key={subcategoryData.id}
                categoryIndex={index}
                subcategoryIndex={subcategoryIndex}
                removeSubcategory={removeSubcategory}
                subcategoriesFieldName={subcategoriesFieldName}
                disabled={disabled}
              />
            );
          })}
        </div>
      )}

      <div>
        <Button
          variant="text"
          size="small"
          onClick={() => appendSubcategory(EMPTY_SUB_CATEGORY_DATA)}
          disabled={disabled || !watchedId}
        >
          Add sub category
        </Button>
      </div>
    </Card>
  );
};

export default CategoriesInputCard;
